/**
 * PublicDashboardShare — Phase 3.
 *
 * Renders the v2 grid from a public share token (no auth required).
 * Uses `axios` directly (not the `api` instance) so the public endpoint
 * doesn't get the Bearer token attached.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Lock, AlertTriangle, Loader2 } from "lucide-react";

const STATUS_CHIP = {
  healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  needs_push: "bg-amber-50 text-amber-800 border-amber-200",
  at_risk: "bg-rose-50 text-rose-700 border-rose-200",
  stale: "bg-slate-100 text-slate-600 border-slate-200",
};
const STATUS_LABEL = {
  healthy: "On Track", needs_push: "Needs Push", at_risk: "At Risk", stale: "Stale",
};

export default function PublicDashboardShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/dashboard/v2/shares/public/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load share"));
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-6">
        <div className="max-w-md text-center" data-testid="public-share-error">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto" />
          <h1 className="font-heading text-2xl mt-3 font-semibold">Share unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">{err}</p>
          <Link to="/" className="text-emerald-700 hover:underline text-sm mt-4 inline-block">Back to Array Engine →</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center" data-testid="public-share-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  const { share, grid } = data;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-emerald-500 grid place-items-center font-heading text-slate-900 font-bold text-sm">AE</div>
            <div>
              <p className="font-heading text-sm font-semibold text-slate-900">Array Engine</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 -mt-0.5">Shared dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Lock className="w-3 h-3" /> View-only
            <span className="mx-1.5">·</span>
            Shared by <span className="font-medium text-slate-700">{share.created_by_name || "Anonymous"}</span>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-5" data-testid="public-share-main">
        <div>
          <p className="label-overline">Dashboard</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight mt-1" data-testid="public-share-label">{share.label}</h1>
          <p className="text-xs text-slate-500 mt-1">
            Shared {share.created_at ? new Date(share.created_at).toLocaleDateString() : ""}
            {share.expires_at && <> · Expires {new Date(share.expires_at).toLocaleDateString()}</>}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="public-share-row1">
          {(grid.row1 || []).map((c) => <PublicKpi key={c.key} card={c} />)}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="public-share-row2">
          {(grid.row2 || []).filter((c) => !c.empty).map((c) => <PublicKpi key={c.key} card={c} />)}
        </div>
        <p className="text-[11px] text-slate-400 text-center pt-6 border-t border-slate-200">
          Powered by <Link to="/" className="text-emerald-700 hover:underline"><Sparkles className="w-3 h-3 inline -mt-0.5" /> Array Engine</Link>
        </p>
      </main>
    </div>
  );
}

function PublicKpi({ card }) {
  const chip = STATUS_CHIP[card.status] || STATUS_CHIP.stale;
  return (
    <div className="bg-white rounded-sm border border-slate-200 p-4" data-testid={`public-share-kpi-${card.key}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="label-overline truncate">{card.label}</p>
        <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded-sm border ${chip}`}>
          {STATUS_LABEL[card.status] || card.status}
        </span>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-heading text-3xl font-semibold text-slate-900">{card.current_display}</span>
        {card.target_display && <span className="text-xs text-slate-400">/ {card.target_display}</span>}
      </div>
      {card.trend_label && <p className="text-[11px] text-slate-500 mt-1.5">{card.trend_label}</p>}
      {card.subtext && <p className="text-[11px] text-slate-500 mt-1.5 leading-snug line-clamp-2">{card.subtext}</p>}
    </div>
  );
}
